import { Drawer, DrawerContent, IconHelp } from "@exopengithub/exokit";
import { SidebarToggleButton } from "@exopengithub/exokit/v2";
import { Link } from "react-router";
import { NavItem } from "./NavItem";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { LanguageNavItem } from "./LanguageNavItem";
import { useTranslation } from "react-i18next";
import { useCollapsibleSidebar } from "./useCollapsibleSidebar";
import { clsx } from "clsx";
import { SidebarContext } from "./SidebarContext";
import { useState } from "react";
import { ExopenSingleLetterLogo } from "./ExopenSingleLetterLogo";
import { SidebarUserSection } from "./SidebarUserSection";
import { useUser } from "../../common/context/userContextUtils";

import { ExopenLogo } from "./ExopenLogo.tsx";

type RenderParams = {
	sidebarOpen: boolean;
	mobileSidebarOpen: boolean;
};

interface SidebarProps {
	empty?: boolean;
	renderContent?: (params: RenderParams) => React.ReactNode;
	renderFooter?: (params: RenderParams) => React.ReactNode;
}

export const Sidebar = ({
	empty,
	renderContent,
	renderFooter = () => null,
}: SidebarProps) => {
	const { t } = useTranslation();
	const user = useUser();
	const { toggleSidebar, open } = useCollapsibleSidebar();

	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

	const isOpen = mobileSidebarOpen ? true : open;

	const sidebarContent = (
		<div
			className={clsx(
				"sticky top-0 h-screen shrink-0 border-[#CAECD2]",
				!empty && "border-r bg-mint-50",
				mobileSidebarOpen ? "block" : "hidden sm:block",
				!mobileSidebarOpen && (open ? "w-[320px]" : "w-[72px]"),
			)}
		>
			<div className="flex h-full flex-col">
				<div
					className={clsx(
						// WHY pt-4 md:pt-6: puts the logo's top edge on the page
						// header's breadcrumb, which starts at HeaderLayoutView's
						// own p-4 md:p-6 content inset.
						"flex items-center gap-2 pt-4 md:pt-6",
						// WHY pl-7 (28px): the left edge shared by the nav icons
						// (mx-4 + pl-3) and the picker card's white inner section
						// (mx-4 + p-3) — the card's border alone bleeds to 16px.
						isOpen ? "pb-5 pl-7 pr-5" : "pb-2 px-6",
					)}
				>
					<div className="grow">
						<Link
							to="/"
							// WHY max-w-31 (124px): ExopenLogo's viewBox is nearly flush
							// with its artwork, so the painted wordmark is ~99% of this
							// width — 124px lands it within a px of Figma's 121px.
							// max-w-36 (144) rendered it 17% oversized.
							className="block max-w-31 grow text-primary-800 transition-opacity hover:opacity-80"
						>
							{isOpen ? <ExopenLogo /> : <ExopenSingleLetterLogo />}
						</Link>
					</div>
					{mobileSidebarOpen ? (
						<button
							type="button"
							className="self-start text-gray-500"
							onClick={() => {
								setMobileSidebarOpen(false);
							}}
						>
							<IconX size={24} />
						</button>
					) : (
						// Only when there's no custom content (empty sidebar) — a real
						// renderContent puts this toggle next to its own Menu header
						// instead, so it stays in the same place open or collapsed.
						isOpen &&
						!renderContent && (
							<SidebarToggleButton
								onClick={toggleSidebar}
								label={t("Collapse sidebar")}
								iconClassName="text-primary-800"
							/>
						)
					)}
				</div>
				{/* Only when there's no custom content (empty sidebar): a real
				 * renderContent (CompanySidebarPart/AllDomainsSidebarPart) puts
				 * this same toggle in the reserved picker-area itself instead,
				 * so nav icons don't shift between open/collapsed. */}
				{!isOpen && !renderContent && (
					<div className="mx-4 mb-1">
						<SidebarToggleButton
							onClick={toggleSidebar}
							label={t("Expand sidebar")}
							iconClassName="text-primary-800"
							className="rounded-xs p-3 hover:bg-white/60"
						/>
					</div>
				)}
				{(renderContent ?? (() => <div className="grow" />))({
					sidebarOpen: isOpen,
					mobileSidebarOpen,
				})}
				<div className="flex flex-col justify-end px-4 pb-6 pt-4">
					{renderFooter({ sidebarOpen: isOpen, mobileSidebarOpen })}
					<div className="mb-2 space-y-1">
						<NavItem
							to="https://www.exopen.se/kontakta-oss"
							label={t("Contact us")}
							icon={<IconHelp />}
						/>
					</div>
					<div className="mb-2 space-y-1">
						<LanguageNavItem />
					</div>
					{user.user && (
						<div className="mt-4">
							<SidebarUserSection sidebarOpen={isOpen} user={user.user} />
						</div>
					)}
				</div>
			</div>
		</div>
	);

	return (
		<SidebarContext.Provider value={{ open: isOpen, toggleSidebar }}>
			<Drawer
				open={mobileSidebarOpen}
				onClose={() => {
					setMobileSidebarOpen(false);
				}}
				position="left"
			>
				<DrawerContent>{sidebarContent}</DrawerContent>
			</Drawer>
			{!mobileSidebarOpen && sidebarContent}
			<div className="sticky top-0 z-10 flex h-[64px] items-center border-b border-[#CAECD2] bg-mint-50 py-[20px] pl-[20px] pr-3 sm:hidden">
				<div className="grow">
					<Link
						to="/"
						className="block text-primary-800 transition-opacity hover:opacity-80"
					>
						<ExopenSingleLetterLogo />
					</Link>
				</div>
				<button
					type="button"
					className="shrink-0 p-2 text-gray-500"
					onClick={() => {
						setMobileSidebarOpen(true);
					}}
				>
					<IconMenu2 size={24} />
				</button>
			</div>
		</SidebarContext.Provider>
	);
};
